import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $document } from "../document-list";
import { docsListItemLocalEntry } from "../document-list/document-list.types";
import {
	type TemplateOnboardingConfig,
	TemplateOnboardingConfigSchema,
} from "../document/template-onboarding.types";
import { env } from "../env";
import { fs } from "../filesystem/filesystem.atom";
import { getDocumentDotMomentPath } from "../paths/paths.internal";

const logger = new Logger("template-config");

// Optimistic draft: holds the latest saved config so the UI updates immediately.
// undefined = not yet written this session (fall through to disk read).
const $templateConfigDraft = atomFamily((_documentId: string) => {
	const draftAtom = atom(undefined as TemplateOnboardingConfig | null | undefined);
	draftAtom.debugPrivate = true;
	return draftAtom;
});

/**
 * Reads the `.moment/template.yml` configuration for a given document.
 *
 * Priority: optimistic draft (set after save) → disk read → null.
 */
export const $templateConfig = atomFamily((documentId: string | undefined) => {
	const $configAtom = atom(async (get): Promise<TemplateOnboardingConfig | null> => {
		if (!documentId || !env.isFilesystemAvailable) {
			return null;
		}

		// Return draft if we've already written this session
		const draft = get($templateConfigDraft(documentId));
		if (draft !== undefined) {
			return draft;
		}

		const doc = get($document(documentId));
		const local = doc ? docsListItemLocalEntry(doc) : undefined;
		if (!local) {
			return null;
		}

		try {
			const dotMomentPath = await getDocumentDotMomentPath(documentId);
			const templateYmlPath = await (
				await import("@tauri-apps/api/path")
			).join(dotMomentPath, "template.yml");

			const existsAtom = fs().exists({ filePath: templateYmlPath });
			const exists = await get(existsAtom);
			if (!exists) {
				return null;
			}

			const yamlAtom = fs().readYamlFile({ filePath: templateYmlPath });
			const raw = await get(yamlAtom);
			return TemplateOnboardingConfigSchema.parse(raw);
		} catch (err) {
			logger.error("Failed to read template.yml", { documentId, err });
			return null;
		}
	});
	$configAtom.debugLabel = `templateConfig/${documentId}`;
	return $configAtom;
});

/**
 * Write-only atom that persists prompts back to `.moment/template.yml`
 * and optimistically updates the in-memory draft so the UI refreshes instantly.
 */
export const $saveTemplateConfig = atom(
	null,
	async (_get, set, params: { documentId: string; config: TemplateOnboardingConfig }) => {
		const { documentId, config } = params;

		const dotMomentPath = await getDocumentDotMomentPath(documentId);
		const templateYmlPath = await (
			await import("@tauri-apps/api/path")
		).join(dotMomentPath, "template.yml");

		// Ensure .moment directory exists
		await set(fs().mkdir, { dirPath: dotMomentPath });

		await set(fs().writeYamlFile, {
			filePath: templateYmlPath,
			content: config,
			documentId,
		});

		// Update draft only after disk write succeeds so the UI never shows unsaved data
		set($templateConfigDraft(documentId), config);

		logger.info("Saved template config", { documentId });
	}
);
$saveTemplateConfig.debugLabel = "saveTemplateConfig";
